<template>
  <div v-if="visible" class="credit-pill-wrap">
    <button
      :id="pillId"
      type="button"
      class="credit-pill"
      :class="isPreview ? 'credit-pill--preview' : ('credit-pill--' + tone)"
      :aria-label="ariaLabel"
      :title="ariaLabel"
    >
      <!-- PREVIEW (SMS not enabled): single illustrative SMS number + tag -->
      <template v-if="isPreview">
        <ChannelIcon channel="sms" class="credit-pill__icon" />
        <span class="credit-pill__count">{{ previewCredits.toLocaleString() }}</span>
        <span class="credit-pill__tag">Preview</span>
      </template>

      <!-- LIVE: SMS · Email side by side -->
      <template v-else>
        <span class="credit-pill__chan credit-pill__chan--sms">
          <ChannelIcon channel="sms" class="credit-pill__icon" :style="{ color: '#0e9bd6' }" />
          <!-- Before the first balance lands, show a loading skeleton — NOT "0" (which reads as
               "no credits"). Mount the counter only once loaded so it appears at the real value
               instead of counting up from 0 on first paint. -->
          <AnimatedCounter v-if="hasBalance" :value="creditsAvailable" class="credit-pill__count" />
          <span v-else class="credit-pill__skeleton" aria-hidden="true"></span>
        </span>
        <template v-if="hasEmail">
          <span class="credit-pill__sep" aria-hidden="true"></span>
          <span class="credit-pill__chan credit-pill__chan--email">
            <ChannelIcon channel="email" class="credit-pill__icon" :style="{ color: '#6f74e0' }" />
            <AnimatedCounter :value="emailAvailable" class="credit-pill__count" />
          </span>
        </template>
        <i class="la la-angle-down credit-pill__caret" aria-hidden="true"></i>
      </template>
    </button>

    <!-- Popover content is portaled to <body>, so its styles live in the NON-scoped block
         below (scoped styles can't reach a portaled node). custom-class scopes them. -->
    <b-popover
      :target="pillId"
      triggers="click blur"
      placement="bottomright"
      boundary="viewport"
      custom-class="credit-pill-popover"
    >
      <!-- PREVIEW (SMS texting not enabled): a static upsell, NOT a live balance — a
           site with no texting number has no wallet, so nothing is queried here. -->
      <div v-if="isPreview" class="cbp">
        <div class="cbp__hero cbp__hero--preview">
          <div class="cbp__hero-label">
            <ChannelIcon channel="sms" /> SMS credits · Preview
          </div>
          <div class="cbp__hero-num">{{ previewCredits.toLocaleString() }}</div>
          <div class="cbp__hero-sub">free credits every month once enabled</div>
        </div>
        <div class="cbp__body">
          <p class="cbp__pitch">
            Text appointment reminders, recalls, and campaigns to your patients —
            {{ previewCredits.toLocaleString() }} free credits every month, plus low-cost top-ups.
          </p>
          <FeatureRequestButton
            class="cbp__buy cbp__buy--enable"
            feature-key="SMS"
            feature-label="2-Way SMS texting"
            source-page="Header - SMS credits"
          />
          <FeatureRequestNote feature="SMS" />
          <a
            class="cbp__docs"
            href="https://docs.teamio.app/billing/costs-and-delivery/"
            target="_blank"
            rel="noopener"
          >How SMS credits work <i class="la la-external-link-alt" aria-hidden="true"></i></a>
        </div>
      </div>

      <!-- LIVE balance (SMS enabled): one card, a section per channel -->
      <div v-else class="cbp">
        <!-- SMS channel -->
        <div class="cbp__seg">
          <div class="cbp__seg-head">
            <span class="cbp__seg-title cbp__seg-title--sms">
              <ChannelIcon channel="sms" /> SMS credits
            </span>
            <span class="cbp__seg-num">{{ creditsAvailable.toLocaleString() }}</span>
          </div>
          <div class="cbp__row">
            <span class="cbp__label">Monthly plan<span
              v-if="paidCredits > 0"
              class="cbp__chip cbp__chip--sms"
            >+{{ paidCredits.toLocaleString() }} purchased</span></span>
            <span class="cbp__val">{{ freeRemainingCredits.toLocaleString() }}</span>
          </div>
          <div v-if="freeAllowance > 0" class="cbp__bar">
            <div class="cbp__bar-fill cbp__bar-fill--sms" :style="{ width: smsUsedPct + '%' }"></div>
          </div>
          <div class="cbp__hint">
            {{ freeRemainingCredits.toLocaleString() }} of {{ freeAllowance.toLocaleString() }} left this month<template v-if="resetDate"> · resets {{ resetDate | dateformat(dateFormatStr) }}</template>
          </div>
        </div>

        <!-- Email channel (display-only until E3) -->
        <div v-if="hasEmail" class="cbp__seg">
          <div class="cbp__seg-head">
            <span class="cbp__seg-title cbp__seg-title--email">
              <ChannelIcon channel="email" /> Email credits
            </span>
            <span class="cbp__seg-num">{{ emailAvailable.toLocaleString() }}</span>
          </div>
          <div class="cbp__row">
            <span class="cbp__label">Monthly plan<span class="cbp__chip cbp__chip--email">included</span></span>
            <span class="cbp__val">{{ emailFreeRemaining.toLocaleString() }}</span>
          </div>
          <div v-if="emailFreeAllowance > 0" class="cbp__bar">
            <div class="cbp__bar-fill cbp__bar-fill--email" :style="{ width: emailUsedPct + '%' }"></div>
          </div>
          <div class="cbp__hint">
            {{ emailFreeRemaining.toLocaleString() }} of {{ emailFreeAllowance.toLocaleString() }} left this month<template v-if="emailResetDate"> · resets {{ emailResetDate | dateformat(dateFormatStr) }}</template>
          </div>
        </div>

        <!-- Footer: neutral buy (channel-agnostic) + how-it-works -->
        <div class="cbp__foot-wrap">
          <button type="button" class="cbp__buy" @click="goBuyCredits">
            <i class="la la-plus-circle" aria-hidden="true"></i> Buy credits
          </button>
          <button type="button" class="cbp__docs cbp__usage" @click="goUsageReport">
            <i class="la la-chart-bar" aria-hidden="true"></i> View your usage report
          </button>
          <a
            class="cbp__docs"
            href="https://docs.teamio.app/billing/costs-and-delivery/"
            target="_blank"
            rel="noopener"
          >How credits &amp; reloads work <i class="la la-external-link-alt" aria-hidden="true"></i></a>
          <div class="cbp__foot">1 SMS credit = 1 text segment · 1 email credit = 1 email</div>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
/**
 * CreditBalancePill — top-right header pill showing the live credit balance for every
 * channel the practice has (SMS · Email today; AI later). Real-time via the wallet store
 * (siteWallet/{siteID} Firestore subscription → `channels.{sms,email}`). Click opens a
 * breakdown popover with a section per channel (monthly allowance, purchased/included,
 * a used-this-month bar) and a channel-agnostic "Buy credits" link to the account area.
 *
 * Gating: for an SMS-enabled site (a provisioned GENERAL_PHONE texting number) it shows
 * the live balance; for a site WITHOUT texting it shows a static PREVIEW/upsell (no wallet
 * query is run — see Layout.initializeCreditBalance). The email section renders only when
 * the wallet doc carries an `email` channel. Rendered only when the wallet feature flag is
 * on AND the user has the messaging/billing permission (Campaigns or Conversations).
 */
import { mapState, mapGetters } from 'vuex';
import AnimatedCounter from './AnimatedCounter.vue';
import ChannelIcon from './ChannelIcon.vue';
import { balanceTone } from '../utils/walletCredits';
import { hasSmsNumber } from '../utils/siteCapability';
import FeatureRequestButton from './FeatureRequestButton/FeatureRequestButton.vue';
import FeatureRequestNote from './FeatureRequestButton/FeatureRequestNote.vue';

// Standard monthly free allowance shown in the non-SMS PREVIEW (mirrors the layer default
// SMS_FREE_MONTHLY_CREDITS = 5000). Illustrative marketing number only — never a live balance.
const PREVIEW_FREE_CREDITS = 5000;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export default {
  name: 'CreditBalancePill',
  components: { AnimatedCounter, ChannelIcon, FeatureRequestButton, FeatureRequestNote },
  // Unique per instance: the header renders the pill twice (the desktop right-side nav and the
  // ≤1499px collapsed bell nav are mutually exclusive, so the pill must live in BOTH to always show)
  // — a distinct id keeps each button's <b-popover> targeting its own trigger.
  props: {
    pillId: { type: String, default: 'credit-balance-pill' },
  },
  computed: {
    ...mapState('wallet', [
      'creditsAvailable',
      'freeAllowance',
      'freeUsed',
      'freeRemainingCredits',
      'paidCredits',
      'resetDate',
      'hasBalance',
    ]),
    ...mapGetters('wallet', ['emailChannel']),
    ...mapState('customer', ['siteID', 'settings']),
    ...mapGetters('customer', ['getSite']),

    // ---- Email channel (display-only; separate 10k/mo pool). Null until the wallet doc
    // carries an `email` channel, in which case the pill/popover show it alongside SMS. ----
    hasEmail() {
      return !this.isPreview && !!this.emailChannel;
    },
    emailAvailable() { return num(this.emailChannel && this.emailChannel.creditsAvailable); },
    emailFreeRemaining() { return num(this.emailChannel && this.emailChannel.freeRemainingCredits); },
    emailFreeAllowance() { return num(this.emailChannel && this.emailChannel.freeAllowance); },
    emailFreeUsed() { return num(this.emailChannel && this.emailChannel.freeUsed); },
    emailResetDate() { return (this.emailChannel && this.emailChannel.resetDate) || null; },

    // Site's preferred date format (mirrors mixin.dateFormatStr) so the reset date reads
    // MM/DD/YYYY or DD/MM/YYYY per the practice's setting, not raw ISO.
    dateFormatStr() {
      const site = this.getSite ? this.getSite(this.siteID) : null;
      return (site && site.DateFormat) || 'MM/DD/YYYY';
    },

    // Site-level PREREQUISITE: SMS is only "on" when the practice has a provisioned
    // texting number (GENERAL_PHONE non-empty) — the SAME signal every other SMS surface
    // gates on (PatientProfile, campaigns, Conversations nav, workflows). A site with no
    // texting number has no credit wallet, so it must never see the balance pill.
    // hasSmsNumber: a WhatsApp-only office keeps its wallet (WhatsApp spends credits).
    smsFeatureEnabled() {
      return hasSmsNumber(this.settings);
    },

    // Settings have actually loaded (customer store fills this on init). Guards against a
    // brief "no GENERAL_PHONE" reading during the pre-settings window.
    settingsLoaded() {
      return Array.isArray(this.settings) && this.settings.length > 0;
    },

    // SMS not enabled → show a static PREVIEW/upsell instead of a live balance.
    isPreview() {
      return !this.smsFeatureEnabled;
    },

    // Standard monthly free allowance shown in the preview (mirrors the layer default
    // SMS_FREE_MONTHLY_CREDITS). Illustrative only — NO per-site query is made for a
    // site that hasn't enabled texting.
    previewCredits() {
      return PREVIEW_FREE_CREDITS;
    },

    // Users who can SEND SMS / see billing: the Campaigns or Conversations
    // permission (the two credit-spending surfaces).
    canView() {
      try {
        return this.$can('view', 'campaigns') || this.$can('view', 'conversations');
      } catch (_) {
        return false;
      }
    },

    // Feature flag; when unset (e.g. env not present) fall back to "a balance doc exists".
    flagEnabled() {
      return process.env.VUE_APP_SMS_WALLET_ENABLED === 'true';
    },

    visible() {
      if (this.$store && this.$store.getters['customer/isDemo'] === true) return false; // credits are not part of the demo
      if (!this.canView) return false;
      // Preview upsell shows once settings have loaded AND the wallet feature is flagged on
      // (the settingsLoaded guard avoids flashing a preview on an SMS site during load); the
      // live balance additionally needs a loaded balance (or the flag) on an SMS-enabled site.
      if (this.isPreview) return this.settingsLoaded && this.flagEnabled;
      return this.flagEnabled || this.hasBalance;
    },

    // Pill colour is driven by the SMS pool — the channel that actually gates sending &
    // costs money (email is a high 10k allowance, display-only). Keeps the glance signal
    // about the thing that can block texts.
    tone() {
      return balanceTone(this.creditsAvailable, this.freeRemainingCredits, this.paidCredits);
    },

    smsUsedPct() {
      if (!this.freeAllowance || this.freeAllowance <= 0) return 0;
      const pct = Math.round((this.freeUsed / this.freeAllowance) * 100);
      return Math.min(100, Math.max(0, pct));
    },

    emailUsedPct() {
      if (!this.emailFreeAllowance || this.emailFreeAllowance <= 0) return 0;
      const pct = Math.round((this.emailFreeUsed / this.emailFreeAllowance) * 100);
      return Math.min(100, Math.max(0, pct));
    },

    ariaLabel() {
      if (this.isPreview) return 'SMS credits — preview';
      if (!this.hasBalance) return 'Loading your credit balance';
      const parts = [`${this.creditsAvailable.toLocaleString()} SMS credits`];
      if (this.hasEmail) parts.push(`${this.emailAvailable.toLocaleString()} email credits`);
      return parts.join(', ') + ' available';
    },
  },
  methods: {
    goBuyCredits() {
      this.goCreditsTab({ tab: 'credits' });
    },
    // "Where did my credits go?" — same Credits tab, with ?view=usage so the tab opens (and scrolls
    // to) the Usage report section. Reuses the routing goBuyCredits already proved out rather than
    // introducing a second navigation pattern.
    goUsageReport() {
      this.goCreditsTab({ tab: 'credits', view: 'usage' });
    },
    goCreditsTab(query) {
      try { this.$root.$emit('bv::hide::popover', this.pillId); } catch (_) { /* popover may already be closed */ }
      const target = { path: '/app/my-account', query };
      if (this.$route.path !== '/app/my-account') {
        this.$router.push(target).catch(() => {});
      } else if (this.$route.query.tab !== query.tab || this.$route.query.view !== query.view) {
        // Already on the page → just switch tab/view (replace, so back doesn't ping-pong).
        this.$router.replace(target).catch(() => {});
      }
    },
  },
};
</script>

<!-- Pill button lives in the component's own DOM → scoped styles apply. -->
<style scoped>
.credit-pill-wrap {
  display: inline-flex;
  align-items: center;
}

.credit-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.28rem 0.62rem;
  border: 1px solid #d7dee4;
  border-radius: 999px;
  background: #f4f7f9;
  color: #2f3a44;
  font-weight: 700;
  font-size: 0.85rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}
.credit-pill:hover { background: #eaf0f4; }

/* Each channel = its icon + number; the icon carries the channel accent (set inline so it
   never depends on scoped matching of the functional icon's root), the number inherits the
   pill's tone colour. */
.credit-pill__chan { display: inline-flex; align-items: center; gap: 0.26rem; }
.credit-pill__sep { width: 1px; height: 0.95rem; background: #cdd6dc; margin: 0 0.04rem; }

/* Loading placeholder shown until the first balance lands — a subtle shimmer in the
   number's shape, so the pill never flashes a misleading "0". Reads on any tone tint. */
.credit-pill__skeleton {
  display: inline-block;
  width: 2.2em;
  height: 0.82em;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(120, 140, 160, 0.20) 25%,
    rgba(120, 140, 160, 0.40) 37%,
    rgba(120, 140, 160, 0.20) 63%
  );
  background-size: 300% 100%;
  animation: credit-pill-shimmer 1.3s ease-in-out infinite;
}
@keyframes credit-pill-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}
@media (prefers-reduced-motion: reduce) {
  .credit-pill__skeleton { animation: none; }
}

.credit-pill__icon { font-size: 0.98rem; }
.credit-pill__caret { font-size: 0.7rem; opacity: 0.55; margin-left: 0.05rem; }

/* Colour states (border + background + fallback text) */
.credit-pill--ok { border-color: #cfe8d8; background: #eef8f1; color: #1f7a49; }
.credit-pill--warn { border-color: #f3dfb8; background: #fdf6e7; color: #97600c; }
.credit-pill--danger { border-color: #f2c4c0; background: #fdeceb; color: #b23b2f; }

/* Preview (SMS not enabled): neutral/muted with a small "Preview" tag. */
.credit-pill--preview { border-color: #d7dee4; background: #f4f7f9; color: #55636d; }
.credit-pill--preview:hover { background: #eaf0f4; }
.credit-pill--preview .credit-pill__count { opacity: 0.9; }
.credit-pill__tag {
  font-size: 0.6rem; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase;
  color: #8a97a1; background: #e7edf1; border-radius: 999px; padding: 1px 6px;
}

/* Responsive: drop the caret on narrow screens, keep icons + numbers. */
@media (max-width: 991px) {
  .credit-pill__caret { display: none; }
  .credit-pill { padding: 0.28rem 0.5rem; }
}
</style>

<!-- Popover is portaled to <body> — these styles MUST be non-scoped, namespaced under the
     custom-class so they never leak to other popovers. -->
<style>
.credit-pill-popover.popover {
  /* Teamio globals set .popover { min-width:350px } + .popover-body { min-width:300px }, and
     min-width BEATS max-width — so we must override min-width too, or the box stays 350px and
     overflows phones. Force a fixed 270px that fits a 320px screen. */
  width: 270px !important;
  min-width: 270px !important;
  max-width: 270px !important;
  border: none;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(20, 40, 60, 0.18);
  overflow: hidden;                 /* clip the hero gradient to the rounded corners */
}
/* Higher specificity than the global `.popover .popover-body { min-width:300px }`, or the body
   stays 300px and its right edge (the values) gets clipped by the 270px box. */
.credit-pill-popover.popover .popover-body {
  padding: 0 !important;
  width: 270px !important;
  min-width: 0 !important;
  max-width: 270px !important;
  color: #2f3a44;
}
.credit-pill-popover .arrow { display: none; }   /* cleaner without the arrow poking the gradient */

.cbp { width: 100% !important; font-size: 0.85rem; }   /* fill the body exactly → never a 1-2px h-scroll */

/* ---- PREVIEW hero (unchanged upsell) ---- */
.cbp__hero {
  padding: 14px 16px 15px;
  color: #fff;
  background: linear-gradient(135deg, #12b39a 0%, #0e9bd6 100%);
}
.cbp__hero--preview { background: linear-gradient(135deg, #12b39a 0%, #0e9bd6 100%); }
.cbp__hero-label {
  display: flex; align-items: center; gap: 0.35rem;
  font-size: 0.7rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; opacity: 0.92;
}
.cbp__hero-num { font-size: 2rem; font-weight: 800; line-height: 1.1; font-variant-numeric: tabular-nums; margin-top: 3px; }
.cbp__hero-sub { font-size: 0.74rem; opacity: 0.85; margin-top: 1px; }

.cbp__body { padding: 12px 16px 14px; }

/* ---- LIVE per-channel segments ---- */
.cbp__seg { padding: 13px 16px 14px; }
.cbp__seg + .cbp__seg { border-top: 1px solid #eef0f3; }
.cbp__seg-head { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; margin-bottom: 8px; }
.cbp__seg-title {
  display: flex; align-items: center; gap: 0.38rem;
  font-size: 0.71rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase;
}
.cbp__seg-title--sms { color: #0e9bd6; }
.cbp__seg-title--email { color: #6f74e0; }
.cbp__seg-num { font-size: 1.3rem; font-weight: 800; font-variant-numeric: tabular-nums; color: #26333d; letter-spacing: -0.01em; }

.cbp__row { display: flex; justify-content: space-between; align-items: center; gap: 0.75rem; }
.cbp__label { color: #55636d; font-weight: 600; }
.cbp__val { font-weight: 800; color: #26333d; font-variant-numeric: tabular-nums; }
.cbp__chip {
  display: inline-block; margin-left: 6px; padding: 1px 7px;
  font-size: 0.6rem; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase;
  border-radius: 999px; vertical-align: middle;
}
.cbp__chip--sms { color: #0e9bd6; background: #e7f6fb; }
.cbp__chip--email { color: #6f74e0; background: #ecebfb; }
.cbp__bar { height: 6px; border-radius: 999px; background: #eceff2; overflow: hidden; margin: 8px 0 4px; }
.cbp__bar-fill { height: 100%; border-radius: 999px; }
.cbp__bar-fill--sms { background: linear-gradient(90deg, #12b39a, #0e9bd6); }
.cbp__bar-fill--email { background: linear-gradient(90deg, #6f74e0, #9a7be0); }
.cbp__hint { font-size: 0.72rem; color: #98a3ad; }

/* ---- Footer (buy + docs + note) ---- */
.cbp__foot-wrap { padding: 12px 16px 14px; background: #fafbfc; border-top: 1px solid #eef0f3; }
.cbp__foot { margin-top: 9px; font-size: 0.67rem; color: #aeb7bf; text-align: center; }

.cbp__buy {
  display: flex; align-items: center; justify-content: center; gap: 0.4rem;
  width: 100%; padding: 0.5rem 0.6rem;
  border: none; border-radius: 8px;
  background: #2f6fed; color: #fff; font-weight: 700; font-size: 0.82rem; cursor: pointer;
  transition: background 0.15s ease;
}
.cbp__buy:hover { background: #245bd0; }
/* Upsell CTA (preview): styled as .cbp__buy but it's an <a>, so kill the underline
   and keep the label white on hover. */
.cbp__buy--enable { text-decoration: none; }
.cbp__buy--enable:hover { color: #fff; text-decoration: none; }

.cbp__pitch { margin: 0 0 4px; font-size: 0.8rem; line-height: 1.45; color: #55636d; }

.cbp__docs {
  display: block; margin-top: 10px; text-align: center;
  font-size: 0.74rem; font-weight: 600; color: #2f6fed; text-decoration: none;
}
.cbp__docs:hover { text-decoration: underline; }
.cbp__docs .la { font-size: 0.7rem; }

/* "View your usage report" is a <button> (it routes, it isn't a URL) but must READ as the link
   beside it. Reset only the button-specific UA defaults and let .cbp__docs supply the typography —
   deliberately NOT `font: inherit`, which (being more specific) would clobber .cbp__docs's size and
   weight. font-family alone needs restating: buttons don't inherit it.
   Namespaced under .credit-pill-popover per this block's own contract — the popover is portaled to
   <body>, so component scoping cannot reach it and containment has to be explicit. */
.credit-pill-popover .cbp__usage {
  background: none;
  border: 0;
  padding: 0;
  width: 100%;
  cursor: pointer;
  font-family: inherit;
}
</style>
